/* oxlint-disable no-console -- IndexedDB/SQLite persistence errors; module has no TelemetryPort */
import { AppConfig } from '@core/domain/AppConfig.ts';
import initSqlJs, { type Database } from 'sql.js';
import { dispatchLocalSqlitePersistFailure } from '@core/domain/localSqlitePersistEvents.ts';

const SCHEDULED_PERSIST_FAILURE_MESSAGE =
  'No se pudo guardar una copia local de la base de datos. Libere espacio en Almacenamiento o sincronice pronto.';

function scheduledPersistFailureMessage(err: unknown): string {
  const hint = err instanceof Error ? err.message.trim() : String(err ?? '').trim();
  return hint ? `${SCHEDULED_PERSIST_FAILURE_MESSAGE} (${hint})` : SCHEDULED_PERSIST_FAILURE_MESSAGE;
}

/**
 * Session PRAGMA: FKs enabled, safer delete (fewer PII remnants when wiping rows), cache tuning.
 * No WAL journal: pairing sql.js persistence with IndexedDB avoids auxiliary WAL quirks.
 */
function configureSqliteClientSession(db: Database): void {
  try {
    db.run('PRAGMA foreign_keys = ON');
    db.run('PRAGMA secure_delete = ON');
    db.run('PRAGMA cache_size = -8000');
    db.run('PRAGMA temp_store = MEMORY');
    db.run('PRAGMA synchronous = NORMAL');
  } catch (e) {
    console.error('[puntos-entrega-sqlite] session PRAGMA', e);
  }
}
import { getSqlJsWasmBinary } from 'sqljs-wasm-browser-binary';
import { getEmbeddedPuntosEntregaDbSnapshot } from 'embedded-puntos-entrega-db-snapshot';
import { withRetry, withTimeout } from '@core/domain/utils/asyncResilience.ts';
// Browser: migrations only (legacy IndexedDB / old snapshots). DDL+seeds → `local.db` + SNAPSHOT_REVISION (see `platforms/database/README.md`).
import { applyPuntosEntregaSqliteMigrations } from '@database/puntosEntregaSqliteMigrations.mjs';
import {
  noteSnapshotRevisionUpgradeIfNeeded,
  readStoredSnapshotRevision,
  writeStoredSnapshotRevision,
} from '@src/modules/shared/local-sqlite/infrastructure/snapshotRevisionGuard.ts';

/**
 * Deletes the IndexedDB database.
 */
function deleteIdbDatabase(): Promise<void> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.deleteDatabase(AppConfig.getLocalPuntosEntregaIdbName());
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error ?? new Error('indexedDB.deleteDatabase failed'));
    req.onblocked = () => {
      /* another tab may keep DB open; onsuccess might be delayed */
    };
  });
}

/** Treat as recoverable snapshot HTTP failures (network, 5xx, 429); do not retry definitive 403/404. */
class SnapshotHttpTransientError extends Error {
  /** Constructs this instance. */

  constructor(readonly status: number) {
    super(`Snapshot HTTP ${status}`);
    this.name = 'SnapshotHttpTransientError';
  }
}

/**
 * Opens the IndexedDB database.
 */
function openIdb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const name = AppConfig.getLocalPuntosEntregaIdbName();
    const version = AppConfig.getLocalPuntosEntregaIdbVersion();
    const store = AppConfig.getLocalPuntosEntregaIdbStore();
    const req = indexedDB.open(name, version);
    req.onerror = () => reject(req.error ?? new Error('indexedDB.open failed'));
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      const idb = req.result;
      if (!idb.objectStoreNames.contains(store)) {
        idb.createObjectStore(store);
      }
    };
  });
}

/**
 * Retrieves bytes from IndexedDB.
 */
async function idbGetBytes(): Promise<Uint8Array | undefined> {
  const idb = await openIdb();
  const store = AppConfig.getLocalPuntosEntregaIdbStore();
  const key = AppConfig.getLocalPuntosEntregaIdbSqliteKey();
  return new Promise((resolve, reject) => {
    const tx = idb.transaction(store, 'readonly');
    const r = tx.objectStore(store).get(key);
    r.onerror = () =>
      reject(r.error instanceof Error ? r.error : new Error(String(r.error ?? 'indexedDB.get failed')));
    r.onsuccess = () => {
      resolve(r.result as Uint8Array | undefined);
    };
    tx.oncomplete = () => idb.close();
  });
}

/**
 * Stores SQLite bytes into IndexedDB.
 */
async function idbPutSqliteBytes(data: Uint8Array): Promise<void> {
  return idbPutBytesAtKey(AppConfig.getLocalPuntosEntregaIdbSqliteKey(), data);
}

/**
 * Stores bytes into IndexedDB under an arbitrary key of the same store.
 */
async function idbPutBytesAtKey(key: string, data: Uint8Array): Promise<void> {
  const idb = await openIdb();
  const store = AppConfig.getLocalPuntosEntregaIdbStore();
  return new Promise((resolve, reject) => {
    // Always close the connection on failure: a leaked handle blocks indexedDB.deleteDatabase forever.
    try {
      const tx = idb.transaction(store, 'readwrite');
      tx.objectStore(store).put(data, key);
      tx.onerror = () => {
        idb.close();
        reject(tx.error instanceof Error ? tx.error : new Error(String(tx.error ?? 'indexedDB transaction failed')));
      };
      tx.oncomplete = () => {
        idb.close();
        resolve();
      };
    } catch (e) {
      idb.close();
      reject(e instanceof Error ? e : new Error(String(e)));
    }
  });
}

const IDB_RECOVERY_BACKUP_KEY_SUFFIX = '.recovery-backup';

/**
 * Preserves unreadable SQLite bytes under a backup key so unsynced local rows are not destroyed
 * by the snapshot reseed; deletes the IndexedDB database only if the backup write itself fails
 * (otherwise the later reseed put could fail against a broken store).
 */
async function backupUnreadableIdbBytes(bytes: Uint8Array): Promise<void> {
  const backupKey = AppConfig.getLocalPuntosEntregaIdbSqliteKey() + IDB_RECOVERY_BACKUP_KEY_SUFFIX;
  try {
    await idbPutBytesAtKey(backupKey, bytes);
  } catch (backupErr) {
    console.error('[puntos-entrega-sqlite] failed to back up unreadable bytes; deleting IndexedDB', backupErr);
    try {
      await deleteIdbDatabase();
    } catch (delErr) {
      console.error('[puntos-entrega-sqlite] failed to delete IndexedDB after open failure', delErr);
    }
  }
}

const SQLJS_WASM_NOT_EMBEDDED_MSG =
  'Este build no incluye WASM de SQLite embebido (caché antigua). Recompile, borre datos del sitio y recargue con red.';

/**
 * Provides initialization options for sql.js (WASM must be inlined at build; no offline HTTP fetch).
 */
function sqlJsInitOptions(): { wasmBinary: Uint8Array } {
  const wasmBinary = getSqlJsWasmBinary();
  if (wasmBinary != null && wasmBinary.byteLength > 0) {
    return { wasmBinary };
  }
  throw new Error(SQLJS_WASM_NOT_EMBEDDED_MSG);
}

/**
 * Asserts that the database is readable by executing a simple query.
 */
function assertDatabaseReadable(db: Database): void {
  try {
    db.run('SELECT 1');
  } catch (e) {
    try {
      db.close();
    } catch {
      /* ignore persistence failures writing revision */
    }
    throw new Error(
      `Local SQLite database is not readable after open: ${e instanceof Error ? e.message : String(e)}`,
    );
  }
}

/**
 * Initializes the sql.js engine.
 */
async function initSqlEngine(): Promise<Awaited<ReturnType<typeof initSqlJs>>> {
  return withRetry(
    (attempt) =>
      withTimeout(
        initSqlJs(sqlJsInitOptions()),
        attempt === 0 ? 35_000 : 45_000,
        'sql.js (WASM) load timed out',
      ),
    {
      maxAttempts: 6,
      baseDelayMs: 300,
      maxDelayMs: 6000,
      jitterRatio: 0.25,
      isRetryable: (err) => {
        const m = err instanceof Error ? err.message : String(err);
        if (/404/.test(m) && /\.wasm|sql-wasm|sql\.js/i.test(m)) return false;
        return true;
      },
    },
  );
}

/**
 * Retrieves bytes from IndexedDB with retry logic.
 */
async function idbGetBytesResilient(): Promise<Uint8Array | undefined> {
  return withRetry(() => idbGetBytes(), {
    maxAttempts: 5,
    baseDelayMs: 80,
    maxDelayMs: 2500,
    jitterRatio: 0.2,
  });
}

/**
 * Checks if the HTTP status indicates a missing snapshot.
 */
function isSnapshotMissingMessage(status: number): boolean {
  return status === 404 || status === 403;
}

/**
 * Fetches the snapshot buffer from the given URL.
 */
async function fetchSnapshotBuffer(dbUrl: string, attemptIndex: number): Promise<ArrayBuffer> {
  const sep = dbUrl.includes('?') ? '&' : '?';
  const url =
    attemptIndex === 0 ? dbUrl : `${dbUrl}${sep}_r=${attemptIndex}&_t=${Date.now()}&cache=bypass`;
  const res = await fetch(url, { cache: 'no-store' });
  if (!res.ok) {
    if (isSnapshotMissingMessage(res.status)) {
      throw new Error(
        `Failed to load ${dbUrl} (${res.status}). Run db:init-local (see deploy docs), then rebuild or start dev so database/local.db is copied to dist.`,
      );
    }
    if (res.status >= 500 || res.status === 429 || res.status === 408) {
      throw new SnapshotHttpTransientError(res.status);
    }
    throw new Error(`Failed to load ${dbUrl} (${res.status}).`);
  }
  return res.arrayBuffer();
}
/**
 * Opens sql.js DB from an in-memory snapshot (embedded bundle bytes or fetched file).
 */
function openDatabaseFromSnapshotBuffer(SQL: Awaited<ReturnType<typeof initSqlJs>>, buf: Uint8Array): Database {
  const db = new SQL.Database(buf);
  // Patches stale IndexedDB bytes (nombre_normalizado backfill); `db:init-local` snapshot is already current.
  applyPuntosEntregaSqliteMigrations(db);
  configureSqliteClientSession(db);
  assertDatabaseReadable(db);
  return db;
}

/**
 * Opens a database from the build-inlined snapshot (offline-first) or fetched `dbUrl` (dev / remote API mode).
 */
async function openDatabaseFromSnapshot(SQL: Awaited<ReturnType<typeof initSqlJs>>, dbUrl: string): Promise<Database> {
  if (AppConfig.useLocalSqliteClient()) {
    const embedded = getEmbeddedPuntosEntregaDbSnapshot();
    if (embedded != null && embedded.byteLength > 0) {
      return openDatabaseFromSnapshotBuffer(SQL, embedded);
    }
  }
  return withRetry(
    async (attemptIndex) => {
      const buf = new Uint8Array(await fetchSnapshotBuffer(dbUrl, attemptIndex));
      return openDatabaseFromSnapshotBuffer(SQL, buf);
    },
    {
      maxAttempts: 6,
      baseDelayMs: 400,
      maxDelayMs: 8000,
      jitterRatio: 0.25,
      isRetryable: (err) => {
        if (err instanceof SnapshotHttpTransientError) return true;
        if (err instanceof TypeError) return true;
        return false;
      },
    },
  );
}

/**
 * Opens a database instance from IndexedDB bytes.
 */
async function openDatabaseFromIdb(SQL: Awaited<ReturnType<typeof initSqlJs>>, bytes: Uint8Array): Promise<Database> {
  const db = new SQL.Database(bytes);
  // In-place repair when SNAPSHOT_REVISION unchanged but schema drifted; re-exports IDB if mutated.
  if (applyPuntosEntregaSqliteMigrations(db)) {
    try {
      await withRetry(() => idbPutSqliteBytes(new Uint8Array(db.export())), {
        maxAttempts: 4,
        baseDelayMs: 100,
        maxDelayMs: 2000,
        jitterRatio: 0.2,
      });
    } catch (e) {
      console.error('[puntos-entrega-sqlite] persist after migration', e);
    }
  }
  configureSqliteClientSession(db);
  assertDatabaseReadable(db);
  return db;
}

/**
 * Loads sql.js-backed SQLite for the PE module: prefers IndexedDB (local mutations); otherwise
 * seeds from the build-inlined snapshot when present, else fetches `LOCAL_PUNTOS_ENTREGA_DB_URL`.
 * When {@link LOCAL_PUNTOS_ENTREGA_SNAPSHOT_REVISION} changes, existing IndexedDB bytes are kept
 * and migrated in place (operational rows are not wiped). Reseed from snapshot only when IDB is empty
 * or open/migrate fails; in the failure case the unreadable bytes are first copied to a
 * `*.recovery-backup` key so unsynced rows remain recoverable.
 */
export async function loadPuntosEntregaSqlDatabase(dbUrl: string): Promise<Database> {
  const SQL = await initSqlEngine();
  const previousRevision = readStoredSnapshotRevision();
  const fromIdb = await idbGetBytesResilient();

  if (fromIdb != null && fromIdb.byteLength > 0) {
    try {
      const db = await openDatabaseFromIdb(SQL, fromIdb);
      writeStoredSnapshotRevision();
      noteSnapshotRevisionUpgradeIfNeeded(previousRevision);
      return db;
    } catch (e) {
      console.error(
        '[puntos-entrega-sqlite] IDB open/migrate failed; backing up local bytes and reseeding from snapshot',
        e,
      );
      await backupUnreadableIdbBytes(fromIdb);
    }
  }

  const db = await openDatabaseFromSnapshot(SQL, dbUrl);
  writeStoredSnapshotRevision();
  noteSnapshotRevisionUpgradeIfNeeded(previousRevision);
  try {
    await idbPutSqliteBytes(new Uint8Array(db.export()));
  } catch (e) {
    console.error('[puntos-entrega-sqlite] initial persist after snapshot', e);
  }
  return db;
}
/**
 * Read-only open: snapshot from `dbUrl` plus migrations; no IndexedDB or `localStorage` revision guard.
 * Bundle mode without persistent SQLite (`USE_LOCAL_PUNTOS_ENTREGA=0`): same embedded DB as offline snapshot.
 */
export async function loadPuntosEntregaSqlDatabaseFromSnapshotOnly(dbUrl: string): Promise<Database> {
  const SQL = await initSqlEngine();
  return openDatabaseFromSnapshot(SQL, dbUrl);
}
/**
 * Creates a debounced function to persist the database to IndexedDB.
 */
export type SqlPersistHandle = {
  schedule: () => void;
  flush: () => Promise<void>;
  hasPendingWrite: () => boolean;
};

export function createDebouncedSqlPersist(db: Database, debounceMs = 120): SqlPersistHandle {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let inFlight: Promise<void> | null = null;

  const write = (): Promise<void> => {
    if (inFlight != null) return inFlight;
    inFlight = (async () => {
      try {
        const data = db.export();
        await idbPutSqliteBytes(data);
      } catch (e) {
        console.error('[puntos-entrega-sqlite] persist', e);
        throw e;
      } finally {
        inFlight = null;
      }
    })();
    return inFlight;
  };

  const schedule = (): void => {
    if (timer != null) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      void write().catch((e) => {
        dispatchLocalSqlitePersistFailure({ phase: 'notify', message: scheduledPersistFailureMessage(e) });
      });
    }, debounceMs);
  };

  const flush = async (): Promise<void> => {
    if (timer != null) {
      clearTimeout(timer);
      timer = null;
    }
    await write();
  };

  const hasPendingWrite = (): boolean => timer != null || inFlight != null;

  return { schedule, flush, hasPendingWrite };
}

export type PuntosEntregaSqlConnection = {
  db: Database;
  persist: () => void;
  flushPersist: () => Promise<void>;
  hasPendingWrite: () => boolean;
};

/** Opens sql.js with IndexedDB persistence from the snapshot URL (debounced `persist` callback). */
export async function openPuntosEntregaSqlConnectionFromSnapshot(
  snapshotUrl: string,
): Promise<PuntosEntregaSqlConnection> {
  const db = await loadPuntosEntregaSqlDatabase(snapshotUrl);
  const handle = createDebouncedSqlPersist(db);
  return {
    db,
    persist: handle.schedule,
    flushPersist: handle.flush,
    hasPendingWrite: handle.hasPendingWrite,
  };
}
